import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { api, formatPrice, COUNTRY_FLAGS } from "@/lib/api";
import { useCompare } from "@/contexts/CompareContext";
import { X, MapPin, Bed, Bath, Maximize, BadgeCheck, Sparkles, ArrowLeft } from "lucide-react";

export default function Compare() {
  const navigate = useNavigate();
  const { ids, remove, clear } = useCompare();
  const [listings, setListings] = useState([]);

  useEffect(() => {
    (async () => {
      if (ids.length === 0) {
        setListings([]);
        return;
      }
      const results = await Promise.all(
        ids.map((id) => api.get(`/listings/${id}`).then((r) => r.data).catch(() => null))
      );
      setListings(results.filter(Boolean));
    })();
  }, [ids]);

  if (ids.length === 0) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <h1 className="font-heading text-4xl font-medium mb-4">Comparateur d'annonces</h1>
          <p className="text-muted-foreground mb-6">Vous n'avez encore sélectionné aucune annonce à comparer.</p>
          <Button onClick={() => navigate("/search")} className="bg-[#D6202C] hover:bg-[#B5181F] rounded-full">
            Parcourir les annonces
          </Button>
        </div>
      </Layout>
    );
  }

  // Build aggregated row data
  const rows = [
    { label: "Pays", get: (l) => `${COUNTRY_FLAGS[l.country]} ${l.country}` },
    { label: "Type", get: (l) => l.type === "vente" ? "Vente" : "Location" },
    { label: "Bien", get: (l) => l.property_type },
    { label: "Prix", get: (l) => formatPrice(l.price, l.currency, l.type), highlight: true },
    { label: "Surface", get: (l) => `${Math.round(l.surface)} m²`, key: "surface", numeric: true },
    { label: "Pièces", get: (l) => l.rooms ?? "—" },
    { label: "Salles de bain", get: (l) => l.bathrooms ?? "—" },
    { label: "Ville", get: (l) => `${l.neighborhood ? l.neighborhood + ", " : ""}${l.city}` },
    { label: "Annonceur", get: (l) => l.owner_name + (l.owner_company ? ` · ${l.owner_company}` : "") },
    { label: "Pro vérifié", get: (l) => l.owner_badge === "pro_verified" ? "Oui" : "Non" },
    { label: "Sponsorisé", get: (l) => l.is_boosted ? "Oui" : "Non" },
    { label: "Équipements", get: (l) => (l.amenities?.length ? l.amenities.join(" · ") : "—") },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-sm text-muted-foreground hover:text-[#D6202C] mb-4">
          <ArrowLeft className="w-4 h-4 me-1" /> Retour
        </button>

        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Comparateur</span>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2">
              {listings.length} annonce{listings.length > 1 ? "s" : ""} comparée{listings.length > 1 ? "s" : ""}
            </h1>
          </div>
          <Button variant="outline" onClick={clear} className="rounded-full">
            Vider la sélection
          </Button>
        </div>

        <div data-testid="compare-table" className="overflow-x-auto">
          <div className="grid gap-4 min-w-[680px]" style={{ gridTemplateColumns: `180px repeat(${listings.length}, minmax(220px, 1fr))` }}>
            {/* Header row */}
            <div />
            {listings.map((l) => (
              <div key={l.id} className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden relative">
                <button
                  onClick={() => remove(l.id)}
                  data-testid={`compare-remove-${l.id}`}
                  className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="aspect-[4/3] bg-[#F5F0EB] overflow-hidden">
                  <img src={l.photos?.[0]} alt={l.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <a href={`/listings/${l.id}`} className="font-heading font-medium text-sm hover:text-[#D6202C] line-clamp-2 block min-h-[2.4rem]">
                    {l.title}
                  </a>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <MapPin className="w-3 h-3" /> {l.city}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2 pt-2 border-t border-[#F0EBE6]">
                    {l.rooms != null && <span className="flex items-center gap-0.5"><Bed className="w-3 h-3" /> {l.rooms}</span>}
                    {l.bathrooms != null && <span className="flex items-center gap-0.5"><Bath className="w-3 h-3" /> {l.bathrooms}</span>}
                    <span className="flex items-center gap-0.5"><Maximize className="w-3 h-3" /> {Math.round(l.surface)}m²</span>
                  </div>
                </div>
              </div>
            ))}

            {/* Data rows */}
            {rows.map((row, i) => {
              const values = listings.map((l) => row.get(l));
              // Find best for numeric rows (lower price = better, larger surface = better)
              let bestIdx = -1;
              if (row.label === "Prix" && listings.length > 1) {
                const prices = listings.map((l) => l.price);
                bestIdx = prices.indexOf(Math.min(...prices));
              } else if (row.key === "surface" && listings.length > 1) {
                const surfaces = listings.map((l) => l.surface);
                bestIdx = surfaces.indexOf(Math.max(...surfaces));
              }
              return (
                <React.Fragment key={row.key || row.label}>
                  <div className="font-medium text-sm text-muted-foreground py-3 px-3 flex items-center">{row.label}</div>
                  {values.map((v, j) => (
                    <div
                      key={listings[j].id}
                      className={`text-sm py-3 px-3 rounded-xl ${
                        row.highlight ? "font-heading font-semibold text-base text-[#D6202C]" : ""
                      } ${bestIdx === j ? "bg-[#11783A]/10 ring-1 ring-[#11783A]/30" : "bg-white border border-[#F0EBE6]"}`}
                    >
                      {v}
                      {bestIdx === j && <Sparkles className="w-3 h-3 inline ms-1 text-[#11783A]" />}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </Layout>
  );
}

import React from "react";
